/**
 * Capa da página /casos/:id/laudo (E13.F8.T02 + T03; refeita na E54.F8.T01).
 *
 * Mostra: title + badge versão `v1` + status global derivado (Rascunho/Em
 * edição/Aprovada) + "Última geração: <data pt-BR>". Botões: Voltar +
 * DOCX/PDF (E27.F1 removeu os placeholders Comparar/Assinar).
 *
 * Extraído de `LaudoCasoPage` para reduzir o tamanho da página abaixo do
 * limite eslint.
 */
import { Badge, Button, MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { CaseRecordData } from '@/lib/cases';

export interface LaudoHeaderProps {
  caseRecord: CaseRecordData;
  /** Narrativas aprovadas E caso homologado (E23.F7) — habilita o download. */
  canDownload: boolean;
  exportingPdf: boolean;
  exportingDocx: boolean;
  downloadBlockedMessage: string | null;
  exportError: string | null;
  globalStatusLabel: string;
  lastGenerated: string;
  onBack: () => void;
  onExportPdf: () => void;
  onExportDocx: () => void;
}

/**
 * O estado global do documento só usa tinta de sinal no extremo: "Aprovada" é
 * resultado e entra na rampa positiva; rascunho e "Em edição" são direção e
 * ficam em aço. Mesma régua do estágio do documento na F7 — e a tag carrega
 * **texto**, nunca só cor.
 */
function statusVariant(label: string): 'success' | 'neutral' {
  return label === ptBR.laudoCaso.headerStatusGlobal.aprovada ? 'success' : 'neutral';
}

export function LaudoHeader({
  caseRecord,
  canDownload,
  exportingPdf,
  exportingDocx,
  downloadBlockedMessage,
  exportError,
  globalStatusLabel,
  lastGenerated,
  onBack,
  onExportPdf,
  onExportDocx,
}: LaudoHeaderProps): JSX.Element {
  const downloadDisabled = !canDownload || exportingPdf || exportingDocx;
  const describedBy = downloadBlockedMessage ? 'laudo-download-blocked' : undefined;
  return (
    <header className="laudo-page__header" data-laudo-section="capa" data-testid="laudo-topbar">
      <div className="flex flex-wrap items-end justify-between gap-sc-4">
        <div>
          <p className="mb-1.5 font-display text-[11px] uppercase tracking-[0.16em] text-fg-dim">
            {ptBR.laudoCaso.headerEyebrow}
          </p>
          <div className="flex flex-wrap items-baseline gap-sc-3">
            <h1 className="m-0 text-display-md text-fg">{ptBR.laudoCaso.pageTitle}</h1>
            {/* Código do caso e versão são dado pericial: mono, como na tabela. */}
            <MonoData variant="muted" data-testid="laudo-display-code">
              {caseRecord.display_code}
            </MonoData>
            <Badge variant="outline" className="font-mono" data-testid="laudo-version-badge">
              {ptBR.laudoCaso.headerVersion}
            </Badge>
            <Badge variant={statusVariant(globalStatusLabel)} data-testid="laudo-global-status">
              {globalStatusLabel}
            </Badge>
          </div>
          <p className="laudo-page__subtitle">{ptBR.laudoCaso.headerSubtitle}</p>
        </div>
        <div className="laudo-page__actions">
          <MonoData variant="muted" className="self-center" data-testid="laudo-last-generated">
            {lastGenerated}
          </MonoData>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onBack}
            data-testid="laudo-back-btn"
          >
            {ptBR.laudoCaso.backToWorkspace}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onExportDocx}
            disabled={downloadDisabled}
            data-testid="laudo-export-docx-btn"
            aria-describedby={describedBy}
          >
            {exportingDocx ? ptBR.laudoCaso.exportingDocx : ptBR.laudoCaso.exportDocx}
          </Button>
          {/* O documento que vai para os autos é a saída da tela: é ele que
              carrega o único preenchimento sólido. */}
          <Button
            type="button"
            size="sm"
            onClick={onExportPdf}
            disabled={downloadDisabled}
            data-testid="laudo-export-btn"
            aria-describedby={describedBy}
          >
            {exportingPdf ? ptBR.laudoCaso.exporting : ptBR.laudoCaso.exportPdf}
          </Button>
        </div>
      </div>
      {downloadBlockedMessage && (
        <p
          id="laudo-download-blocked"
          role="status"
          className="laudo-page__hint"
          data-testid="laudo-download-blocked"
        >
          {downloadBlockedMessage}
        </p>
      )}
      {exportError && (
        <p role="alert" className="laudo-page__error">
          {exportError}
        </p>
      )}
    </header>
  );
}
